
import React, { useEffect, useState } from "react";
import useAvailability from "../hooks/useAvailability";
import LoadingSpinner from "./LoadingSpinner";
import {
  BUSINESS_TIME_ZONE_LABEL,
  formatBusinessTime,
  getBusinessToday,
  isPastBusinessDate,
} from "../services/utils/dateUtils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pad = (n) => String(n).padStart(2, "0");

// 'YYYY-MM-DD' for a day of the displayed month. Days are Bahamas calendar
// days (contract v1 §1.3), so no Date/time-zone conversion is involved.
const dateKeyFor = (year, monthIndex, dayNum) => `${year}-${pad(monthIndex + 1)}-${pad(dayNum)}`;

/**
 * Month calendar + slot list for one service.
 *
 * Every button is type="button": the picker renders inside the checkout
 * <form>, and a default (submit) button would book the previously selected
 * slot when a day, month or time is clicked.
 *
 * @param {object} props
 * @param {string} props.serviceSlug - Bookable service slug
 * @param {Function} props.onSelect - Called with the chosen slot {startISO, endISO}
 * @param {string|null} [props.selectedStartISO] - startISO of the slot currently chosen
 * @param {Function} [props.onDayChange] - Called with the new 'YYYY-MM-DD' when the day changes
 * @param {boolean} [props.disabled] - Lock days and slots (e.g. while a booking is being sent)
 * @param {number} [props.refreshKey] - Change it to refetch the day's slots bypassing the cache
 */
export default function AvailabilityPicker({
  serviceSlug,
  onSelect,
  selectedStartISO = null,
  onDayChange,
  disabled = false,
  refreshKey = 0,
}) {
  const [month, setMonth] = useState(() => {
    const [y, m] = getBusinessToday().split("-").map(Number);
    return { year: y, monthIndex: m - 1 };
  });
  const [day, setDay] = useState(null); // 'YYYY-MM-DD'

  const { slots, loading, error, refetch } = useAvailability(serviceSlug, day);

  // Parent asks for fresh slots (e.g. after 409 slot_unavailable).
  useEffect(() => {
    if (refreshKey) refetch();
  }, [refreshKey, refetch]);

  const { year, monthIndex } = month;
  const monthTitle = new Date(year, monthIndex, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  const startWeekday = new Date(year, monthIndex, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const shiftMonth = (delta) => {
    const d = new Date(year, monthIndex + delta, 1);
    setMonth({ year: d.getFullYear(), monthIndex: d.getMonth() });
  };

  const selectDay = (key) => {
    if (key === day) return;
    setDay(key);
    onDayChange?.(key);
  };

  return (
    <div className="availability">
      {/* header */}
      <div className="availability__bar">
        <button type="button" className="mini" onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
        <div className="availability__title" aria-live="polite">{monthTitle}</div>
        <button type="button" className="mini" onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
      </div>

      {/* week labels */}
      <div className="availability__grid grid-7 muted" aria-hidden="true">
        {WEEKDAYS.map((d) => (
          <div key={d} style={{textAlign:"center"}}>{d}</div>
        ))}
      </div>

      {/* days */}
      <div className="availability__grid grid-7">
        {Array.from({ length: startWeekday }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const d = i + 1;
          const key = dateKeyFor(year, monthIndex, d);
          const isSelected = key === day;
          const isPast = isPastBusinessDate(key);
          const weekday = WEEKDAYS[new Date(year, monthIndex, d).getDay()];

          return (
            <button
              type="button"
              key={key}
              onClick={() => selectDay(key)}
              className={`day ${isSelected ? "selected" : ""} ${isPast ? "disabled" : ""}`}
              aria-pressed={isSelected}
              aria-label={`${weekday} ${monthTitle.split(" ")[0]} ${d}`}
              disabled={isPast || disabled}
              style={isPast ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              {d}
            </button>
          );
        })}
      </div>

      {/* times */}
      <div className="availability__times">
        <h4>Available times <span className="muted">({BUSINESS_TIME_ZONE_LABEL})</span></h4>
        {loading ? (
          <LoadingSpinner size="small" text="Loading available times..." />
        ) : error ? (
          <div role="alert" style={{ color: '#d32f2f', padding: '1rem' }}>
            <p>{error}</p>
            <p className="muted" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
              Please try again or contact support if this persists.
            </p>
          </div>
        ) : day ? (
          slots.length ? (
            <div className="slots">
              {slots.map((s) => {
                const label = formatBusinessTime(s.startISO);
                const isSelected = s.startISO === selectedStartISO;
                return (
                  <button
                    type="button"
                    key={s.startISO}
                    className={`slot ${isSelected ? "selected" : ""}`}
                    onClick={() => onSelect?.(s)}
                    aria-pressed={isSelected}
                    disabled={disabled}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="muted">No times available for this day.</p>
          )
        ) : (
          <p className="muted">Choose a day to see times.</p>
        )}
      </div>
    </div>
  );
}
